import { doc, getDoc, setDoc, deleteDoc, writeBatch, updateDoc } from 'firebase/firestore';
import { sanitizeData } from '../utils/sanitizeData';

import { db } from '../firebase';
import type { Warband, Model, Item, ExplorationLocation, ModelTemplate, ItemTemplate } from '../types';

export interface SaveData {
  warband: Warband;
  models: Model[];
  items: Item[];
  locations: ExplorationLocation[];
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
}

interface ProfileMetadata {
  name: string;
  createdAt: Date;
  updatedAt: Date;
  lastAccessedAt: Date;
}

/**
 * Save a complete profile to Firebase using granular documents
 * Data is split across 6 collections for better performance and reliability
 */
export async function saveProfileToFirebase(
  userId: string,
  profileId: string,
  data: SaveData,
  profileName: string
): Promise<void> {
  if (!userId || !profileId) {
    throw new Error('userId and profileId are required');
  }

  // Sanitize all data before saving
  const cleanData = {
    warband: sanitizeData(data.warband),
    models: sanitizeData(data.models),
    items: sanitizeData(data.items),
    locations: sanitizeData(data.locations),
    modelTemplates: sanitizeData(data.modelTemplates),
    itemTemplates: sanitizeData(data.itemTemplates),
  };

  const now = new Date();

  // Use batch write for atomicity (all succeed or all fail)
  const batch = writeBatch(db);

  // 1. Profile metadata
  const profileRef = doc(db, `users/${userId}/profiles/${profileId}`);
  batch.set(profileRef, {
    name: profileName,
    createdAt: now,
    updatedAt: now,
    lastAccessedAt: now,
  } as ProfileMetadata, { merge: true });

  // 2. Warband data
  const warbandRef = doc(db, `users/${userId}/warbands/${profileId}`);
  batch.set(warbandRef, cleanData.warband);

  // 3. Models array
  const modelsRef = doc(db, `users/${userId}/models/${profileId}_models`);
  batch.set(modelsRef, { models: cleanData.models });

  // 4. Items array
  const itemsRef = doc(db, `users/${userId}/items/${profileId}_items`);
  batch.set(itemsRef, { items: cleanData.items });

  // 5. Locations array
  const locationsRef = doc(db, `users/${userId}/locations/${profileId}_locations`);
  batch.set(locationsRef, { locations: cleanData.locations });

  // 6. Templates
  const templatesRef = doc(db, `users/${userId}/templates/${profileId}_templates`);
  batch.set(templatesRef, {
    modelTemplates: cleanData.modelTemplates,
    itemTemplates: cleanData.itemTemplates,
  });

  // Commit all writes atomically
  await batch.commit();

  console.log(`✅ Profile ${profileId} saved successfully to Firebase`);
}

/**
 * Save only specific collections that changed (more efficient)
 */
export async function savePartialProfileToFirebase(
  userId: string,
  profileId: string,
  changes: Map<string, any>
): Promise<void> {
  if (!userId || !profileId) {
    throw new Error('userId and profileId are required');
  }

  const batch = writeBatch(db);
  const now = new Date();

  // Always update profile metadata timestamp
  const profileRef = doc(db, `users/${userId}/profiles/${profileId}`);
  batch.update(profileRef, { updatedAt: now, lastAccessedAt: now });

  // Only save collections that changed
  for (const [type, changeData] of Array.from(changes.entries())) {
    const cleanData = sanitizeData(changeData.data);

    switch (type) {
      case 'warband':
        const warbandRef = doc(db, `users/${userId}/warbands/${profileId}`);
        batch.set(warbandRef, cleanData);
        break;

      case 'models':
        const modelsRef = doc(db, `users/${userId}/models/${profileId}_models`);
        batch.set(modelsRef, { models: cleanData });
        break;

      case 'items':
        const itemsRef = doc(db, `users/${userId}/items/${profileId}_items`);
        batch.set(itemsRef, { items: cleanData });
        break;

      case 'locations':
        const locationsRef = doc(db, `users/${userId}/locations/${profileId}_locations`);
        batch.set(locationsRef, { locations: cleanData });
        break;

      case 'templates':
        const templatesRef = doc(db, `users/${userId}/templates/${profileId}_templates`);
        batch.set(templatesRef, {
          modelTemplates: cleanData.modelTemplates,
          itemTemplates: cleanData.itemTemplates,
        });
        break;
    }
  }

  await batch.commit();
  console.log(`✅ Partial save completed for ${Array.from(changes.keys()).join(', ')}`);
}

/**
 * Load a complete profile from Firebase
 */
export async function loadProfileFromFirebase(
  userId: string,
  profileId: string
): Promise<SaveData | null> {
  if (!userId || !profileId) {
    throw new Error('userId and profileId are required');
  }

  try {
    // Load all documents in parallel for better performance
    const [profileDoc, warbandDoc, modelsDoc, itemsDoc, locationsDoc, templatesDoc] = await Promise.all([
      getDoc(doc(db, `users/${userId}/profiles/${profileId}`)),
      getDoc(doc(db, `users/${userId}/warbands/${profileId}`)),
      getDoc(doc(db, `users/${userId}/models/${profileId}_models`)),
      getDoc(doc(db, `users/${userId}/items/${profileId}_items`)),
      getDoc(doc(db, `users/${userId}/locations/${profileId}_locations`)),
      getDoc(doc(db, `users/${userId}/templates/${profileId}_templates`)),
    ]);

    // Check if profile exists
    if (!profileDoc.exists()) {
      return null;
    }

    // Update last accessed timestamp
    updateDoc(profileDoc.ref, { lastAccessedAt: new Date() });

    // Reconstruct the complete save data
    const saveData: SaveData = {
      warband: warbandDoc.exists() ? (warbandDoc.data() as Warband) : {} as Warband,
      models: modelsDoc.exists() ? (modelsDoc.data().models || []) : [],
      items: itemsDoc.exists() ? (itemsDoc.data().items || []) : [],
      locations: locationsDoc.exists() ? (locationsDoc.data().locations || []) : [],
      modelTemplates: templatesDoc.exists() ? (templatesDoc.data().modelTemplates || []) : [],
      itemTemplates: templatesDoc.exists() ? (templatesDoc.data().itemTemplates || []) : [],
    };

    console.log(`✅ Profile ${profileId} loaded successfully from Firebase`);
    return saveData;
  } catch (error) {
    console.error('❌ Error loading profile from Firebase:', error);
    throw error;
  }
}

/**
 * Delete a profile and all its associated data from Firebase
 */
export async function deleteProfileFromFirebase(
  userId: string,
  profileId: string
): Promise<void> {
  if (!userId || !profileId) {
    throw new Error('userId and profileId are required');
  }

  const batch = writeBatch(db);

  // Delete all documents associated with this profile
  batch.delete(doc(db, `users/${userId}/profiles/${profileId}`));
  batch.delete(doc(db, `users/${userId}/warbands/${profileId}`));
  batch.delete(doc(db, `users/${userId}/models/${profileId}_models`));
  batch.delete(doc(db, `users/${userId}/items/${profileId}_items`));
  batch.delete(doc(db, `users/${userId}/locations/${profileId}_locations`));
  batch.delete(doc(db, `users/${userId}/templates/${profileId}_templates`));

  await batch.commit();

  console.log(`✅ Profile ${profileId} deleted successfully from Firebase`);
}

/**
 * Verify that a save operation was successful
 */
export async function verifySave(userId: string, profileId: string): Promise<boolean> {
  try {
    const [profileDoc, warbandDoc] = await Promise.all([
      getDoc(doc(db, `users/${userId}/profiles/${profileId}`)),
      getDoc(doc(db, `users/${userId}/warbands/${profileId}`)),
    ]);

    return profileDoc.exists() && warbandDoc.exists();
  } catch (error) {
    console.error('❌ Error verifying save:', error);
    return false;
  }
}
